import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/auth/Login';
import ImpersonateCallback from './components/auth/ImpersonateCallback';
import ResetPassword from './components/auth/ResetPassword';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import Loading from './components/common/Loading';
import SessionWarningModal from './components/common/SessionWarningModal';
import CambiarPasswordModal from './components/common/CambiarPasswordModal';
import useSessionTimeout from './hooks/useSessionTimeout';
import { getDefaultPath } from './config/profilesConfig';
import api, { removeToken } from './services/api';
import { getEncuestasEstudiante } from './services/encuestasService';
import { getEncuestasDisponibles } from './services/encuestasPlataformaService';
import './App.css';

// =============================================================================
// Lazy-loaded views (code splitting por rol)
// =============================================================================

// ROOT SISTEMA
const DashboardRoot = lazy(() => import('./components/root/DashboardRoot'));
const ConfigColegio = lazy(() => import('./components/root/ConfigColegio'));
const Perfiles = lazy(() => import('./components/root/Perfiles'));
const ConfiguracionCorreo = lazy(() => import('./components/root/ConfiguracionCorreo'));
const UsuariosSistema = lazy(() => import('./components/root/UsuariosSistema'));
const TestingRoot = lazy(() => import('./components/root/TestingRoot'));
const DocsRoot = lazy(() => import('./components/root/DocsRoot'));
const PropuestaComercial = lazy(() => import('./components/root/PropuestaComercial'));
const PrematriculaPublica = lazy(() => import('./components/publico/PrematriculaPublica'));
const PrematriculaEstudiante = lazy(() => import('./components/estudiante/PrematriculaEstudiante'));
const EncuestasPlataforma = lazy(() => import('./components/root/EncuestasPlataforma'));
const FinancieroPlataforma = lazy(() => import('./components/root/FinancieroPlataforma'));

// ROOT COLEGIO
const UsuariosColegio = lazy(() => import('./components/root/UsuariosColegio'));
const PerfilesColegio = lazy(() => import('./components/root/PerfilesColegio'));
const ConfiguracionColegio = lazy(() => import('./components/root/ConfiguracionColegio'));
const AdminDatos = lazy(() => import('./components/root/AdminDatos'));
const DashboardRootColegio = lazy(() => import('./components/root/DashboardRootColegio'));

// ADMIN
const AreasAdmin = lazy(() => import('./components/admin/AreasAdmin'));
const MateriasAdmin = lazy(() => import('./components/admin/MateriasAdmin'));
const IntensidadHoraria = lazy(() => import('./components/admin/IntensidadHoraria'));
const HorariosAdmin = lazy(() => import('./components/admin/HorariosAdmin'));
const CursosAdmin = lazy(() => import('./components/admin/CursosAdmin'));
const MallasList = lazy(() => import('./components/mallas/MallasList'));
const MallaEditor = lazy(() => import('./components/mallas/MallaEditor'));
const MallasDashboard = lazy(() => import('./components/mallas/MallasDashboard'));
const CuadroHonor = lazy(() => import('./components/admin/CuadroHonor'));
const CargaAcademica = lazy(() => import('./components/admin/CargaAcademica'));
const DashboardAdmin = lazy(() => import('./components/admin/DashboardAdmin'));
const ContenidosAdmin = lazy(() => import('./components/admin/ContenidosAdmin'));
const EscalasNotasAdmin = lazy(() => import('./components/admin/EscalasNotasAdmin'));
const NotaAprobacionAdmin = lazy(() => import('./components/admin/NotaAprobacionAdmin'));
const PeriodosAdmin = lazy(() => import('./components/admin/PeriodosAdmin'));
const ConfigFinancieroAdmin = lazy(() => import('./components/admin/ConfigFinancieroAdmin'));
const CircularesAdmin = lazy(() => import('./components/admin/CircularesAdmin'));
const ModulosPerfilAdmin = lazy(() => import('./components/admin/ModulosPerfilAdmin'));
const ConfigNotasAdmin = lazy(() => import('./components/admin/ConfigNotasAdmin'));
const ConfigAulaVirtualAdmin = lazy(() => import('./components/admin/ConfigAulaVirtualAdmin'));
const BloquearNotasAdmin = lazy(() => import('./components/admin/BloquearNotasAdmin'));
const InasistenciasAdmin = lazy(() => import('./components/admin/InasistenciasAdmin'));
const PrematriculasAdmin = lazy(() => import('./components/admin/PrematriculasAdmin'));
const FinancieroAdmin = lazy(() => import('./components/admin/FinancieroAdmin'));
const EleccionesAdmin = lazy(() => import('./components/admin/EleccionesAdmin'));
const SoporteAdmin = lazy(() => import('./components/admin/SoporteAdmin'));
const GenerarBoletines = lazy(() => import('./components/admin/GenerarBoletines'));
const ConfigBoletines = lazy(() => import('./components/admin/ConfigBoletines'));
const FotosEstudiantesAdmin = lazy(() => import('./components/admin/FotosEstudiantesAdmin'));
const CarnetsAdmin = lazy(() => import('./components/admin/CarnetsAdmin'));
const GenerarCertificados = lazy(() => import('./components/admin/GenerarCertificados'));
const CaracterizacionAdmin = lazy(() => import('./components/admin/CaracterizacionAdmin'));
const CaracterizacionV2Admin = lazy(() => import('./components/admin/CaracterizacionV2Admin'));
const AgendamientosAdmin = lazy(() => import('./components/admin/AgendamientosAdmin'));
const EncuestasAdmin = lazy(() => import('./components/admin/EncuestasAdmin'));
const ExportarDatosAdmin = lazy(() => import('./components/admin/ExportarDatosAdmin'));
const DireccionCursoAdmin = lazy(() => import('./components/admin/DireccionCursoAdmin'));
const SeguimientoAdmin = lazy(() => import('./components/admin/SeguimientoAdmin'));
const ReporteGlobalAdmin = lazy(() => import('./components/admin/ReporteGlobalAdmin'));

// DOCENTE
const DashboardDocente = lazy(() => import('./components/docente/DashboardDocente'));
const NotasDocente = lazy(() => import('./components/docente/NotasDocente'));
const AulaVirtualDocente = lazy(() => import('./components/docente/AulaVirtualDocente'));
const InasistenciasDocente = lazy(() => import('./components/docente/InasistenciasDocente'));
const AgendaDocente = lazy(() => import('./components/docente/AgendaDocente'));
const BibliotecaDocente = lazy(() => import('./components/docente/BibliotecaDocente'));
const ObservacionesDocente = lazy(() => import('./components/docente/ObservacionesDocente'));
const AgendamientosDocente = lazy(() => import('./components/docente/AgendamientosDocente'));
const NivelacionesDocente = lazy(() => import('./components/docente/NivelacionesDocente'));
const ReporteNotasDocente = lazy(() => import('./components/docente/ReporteNotasDocente'));
const SabanaNotasDocente = lazy(() => import('./components/docente/SabanaNotasDocente'));
const PromediosAcumuladosDocente = lazy(() => import('./components/docente/PromediosAcumuladosDocente'));
const ReprobadosDocente = lazy(() => import('./components/docente/ReprobadosDocente'));
const SalasVirtualesDocente = lazy(() => import('./components/docente/SalasVirtualesDocente'));
const SoporteDocente = lazy(() => import('./components/docente/SoporteDocente'));
const DesempenosDocente = lazy(() => import('./components/docente/DesempenosDocente'));
const VistaPreviaBoletinesDocente = lazy(() => import('./components/docente/VistaPreviaBoletinesDocente'));
const ObservacionesBoletinDocente = lazy(() => import('./components/docente/ObservacionesBoletinDocente'));
const CaracterizacionDocente = lazy(() => import('./components/docente/CaracterizacionDocente'));
const CaracterizacionV2Docente = lazy(() => import('./components/docente/CaracterizacionV2Docente'));
const EleccionesDocente = lazy(() => import('./components/docente/EleccionesDocente'));
const EncuestasDocente = lazy(() => import('./components/docente/EncuestasDocente'));
const CircularesDocente = lazy(() => import('./components/docente/CircularesDocente'));

// ESTUDIANTE
const DashboardEstudiante = lazy(() => import('./components/estudiante/DashboardEstudiante'));
const AulaVirtualEstudiante = lazy(() => import('./components/estudiante/AulaVirtualEstudiante'));
const NotasEstudiante = lazy(() => import('./components/estudiante/NotasEstudiante'));
const HorarioEstudiante = lazy(() => import('./components/estudiante/HorarioEstudiante'));
const FallasEstudiante = lazy(() => import('./components/estudiante/AsistenciaEstudiante'));
const AgendaEstudiante = lazy(() => import('./components/estudiante/AgendaEstudiante'));
const BibliotecaEstudiante = lazy(() => import('./components/estudiante/BibliotecaEstudiante'));
const ObservacionesEstudiante = lazy(() => import('./components/estudiante/ObservacionesEstudiante'));
const CircularesEstudiante = lazy(() => import('./components/estudiante/CircularesEstudiante'));
const FinancieroEstudiante = lazy(() => import('./components/estudiante/FinancieroEstudiante'));
const AgendamientosEstudiante = lazy(() => import('./components/estudiante/AgendamientosEstudiante'));
const EleccionesEstudiante = lazy(() => import('./components/estudiante/EleccionesEstudiante'));
const PerfilEstudiante = lazy(() => import('./components/estudiante/PerfilEstudiante'));
const BoletinesEstudiante = lazy(() => import('./components/estudiante/BoletinesEstudiante'));
const EncuestasEstudiante = lazy(() => import('./components/estudiante/EncuestasEstudiante'));

// SOPORTE
const BandejaSoporte1 = lazy(() => import('./components/soporte/BandejaSoporte1'));
const BandejaSoporte2 = lazy(() => import('./components/soporte/BandejaSoporte2'));
const DashboardSoporte = lazy(() => import('./components/soporte/DashboardSoporte'));

// Configuracion de sesion
const SESSION_CONFIG = {
  timeoutMinutes: 30,
  warningSeconds: 60,
  showWarning: true
};

function App() {
  const [isLogged, setIsLogged] = useState(false);
  const [user, setUser] = useState(null);
  const [showCambiarPassword, setShowCambiarPassword] = useState(false);
  const [encuestasPendientes, setEncuestasPendientes] = useState(0);
  // Prematrícula de renovación OBLIGATORIA sin responder: el estudiante solo ve esa pantalla
  const [prematriculaPendiente, setPrematriculaPendiente] = useState(false);

  // Verificar si hay sesion guardada al cargar la app
  // Primero buscar en sessionStorage (sesiones de impersonation, específicas de pestaña)
  // Luego en localStorage (sesiones normales, compartidas entre pestañas)
  useEffect(() => {
    // Priorizar sessionStorage para sesiones de impersonation
    const sessionUser = sessionStorage.getItem('user');
    const localUser = localStorage.getItem('user');
    const savedUser = sessionUser || localUser;

    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      setIsLogged(true);
      // Si debe cambiar contraseña, mostrar modal
      if (parsedUser.debeCambiarPassword) {
        setShowCambiarPassword(true);
      }
    }
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsLogged(true);
    // Si debe cambiar contraseña, mostrar modal
    if (userData.debeCambiarPassword) {
      setShowCambiarPassword(true);
    }
  };

  const handlePasswordChanged = () => {
    // Actualizar el estado del usuario
    setUser(prev => ({ ...prev, debeCambiarPassword: false }));
    setShowCambiarPassword(false);
  };

  const handleLogout = () => {
    // Limpiar ambos storages
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('isImpersonated');
    localStorage.removeItem('user');
    removeToken(); // Eliminar token JWT de ambos storages
    setUser(null);
    setIsLogged(false);
    window.location.replace('/');
  };

  // Verificar encuestas pendientes para estudiantes
  useEffect(() => {
    const verificarEncuestas = async () => {
      if (!user || user.role !== 'Estudiante' || !user.esquema) return;
      try {
        const [resInst, resPlat] = await Promise.allSettled([
          getEncuestasEstudiante(user.esquema),
          getEncuestasDisponibles(user.esquema)
        ]);
        const inst = resInst.status === 'fulfilled' ? (resInst.value.encuestas || []) : [];
        const plat = resPlat.status === 'fulfilled' ? (resPlat.value.encuestas || []) : [];
        const pendientes = [...inst, ...plat].filter(e => !e.ya_respondio);
        setEncuestasPendientes(pendientes.length);
      } catch (err) {
        console.error('Error verificando encuestas:', err);
        setEncuestasPendientes(0);
      }
    };
    verificarEncuestas();
  }, [user]);

  // Verificar si el estudiante debe responder la prematrícula obligatoria.
  // No se bloquea una sesión de impersonation (soporte/Admin revisando como el estudiante).
  useEffect(() => {
    if (!user || user.role !== 'Estudiante' || !user.esquema || user.isImpersonated) {
      setPrematriculaPendiente(false);
      return;
    }
    api.get(`/prematriculas/${user.esquema}/mi-renovacion/estado`)
      .then(r => setPrematriculaPendiente(!!(r && r.pendiente)))
      .catch(() => setPrematriculaPendiente(false)); // ante un error, nunca bloquear
  }, [user]);

  // Hook de timeout de sesion
  const {
    showWarningModal,
    remainingSeconds,
    extendSession,
    confirmLogout
  } = useSessionTimeout({
    timeoutMinutes: SESSION_CONFIG.timeoutMinutes,
    warningSeconds: SESSION_CONFIG.warningSeconds,
    showWarning: SESSION_CONFIG.showWarning,
    onLogout: handleLogout,
    isActive: isLogged
  });

  // Inscripción pública por link/QR: funciona con o sin sesión y sin el layout de la app
  if (window.location.pathname.startsWith('/inscripcion/')) {
    return (
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/inscripcion/:token" element={<PrematriculaPublica />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  if (!isLogged) {
    return (
      <BrowserRouter>
        <Routes>
          {/* Ruta especial para impersonation (acceso desde Root Sistema) */}
          <Route path="/impersonate" element={<ImpersonateCallback onLoginSuccess={handleLoginSuccess} />} />
          {/* Ruta para restablecer contraseña */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Obtener ruta por defecto segun el perfil
  const defaultPath = getDefaultPath(user.role, user.tipoUsuario);

  return (
    <BrowserRouter>
      <ErrorBoundary>
      <Layout user={user} onLogout={handleLogout} onCambiarPassword={() => setShowCambiarPassword(true)}>
        <Suspense fallback={<Loading />}>
        <Routes>
          {/* Redirigir raiz a la ruta por defecto */}
          <Route path="/" element={<Navigate to={defaultPath} replace />} />

          {/* ===== RUTAS ROOT SISTEMA ===== */}
          {user.role === 'Root' && user.tipoUsuario === 'SISTEMA' && [
            <Route key="rs-dashboard" path="/dashboard" element={<DashboardRoot />} />,
            <Route key="rs-colegios" path="/colegios" element={<ConfigColegio />} />,
            <Route key="rs-correo" path="/correo" element={<ConfiguracionCorreo />} />,
            <Route key="rs-testing" path="/testing" element={<TestingRoot />} />,
            <Route key="rs-docs" path="/docs" element={<DocsRoot />} />,
            <Route key="rs-propuesta" path="/propuesta" element={<PropuestaComercial />} />,
            <Route key="rs-soporte" path="/soporte" element={<DashboardSoporte user={user} />} />,
            <Route key="rs-encuestas" path="/encuestas" element={<EncuestasPlataforma user={user} />} />,
            <Route key="rs-financiero" path="/financiero-plataforma" element={<FinancieroPlataforma />} />
          ]}

          {/* ===== RUTAS ROOT SISTEMA + ROOT COLEGIO (compartidas) ===== */}
          {user.role === 'Root' && [
            <Route key="perfiles" path="/perfiles" element={
              user.tipoUsuario === 'SISTEMA' ? <Perfiles /> : <PerfilesColegio />
            } />,
            <Route key="usuarios" path="/usuarios" element={
              user.tipoUsuario === 'SISTEMA' ? <UsuariosSistema /> : <UsuariosColegio />
            } />
          ]}

          {/* ===== RUTAS ROOT COLEGIO ===== */}
          {user.role === 'Root' && user.tipoUsuario === 'COLEGIO' && [
            <Route key="rc-dashboard" path="/dashboard" element={<DashboardRootColegio user={user} />} />,
            <Route key="rc-configuracion" path="/configuracion" element={<ConfiguracionColegio />} />,
            <Route key="rc-admin-datos" path="/admin-datos" element={<AdminDatos />} />
          ]}

          {/* ===== RUTAS ADMIN ===== */}
          {user.role === 'Admin' && [
            <Route key="dashboard" path="/dashboard" element={<DashboardAdmin user={user} />} />,
            <Route key="areas" path="/administracion/areas" element={<AreasAdmin />} />,
            <Route key="materias" path="/administracion/materias" element={<MateriasAdmin />} />,
            <Route key="intensidad-horaria" path="/administracion/intensidad-horaria" element={<IntensidadHoraria />} />,
            <Route key="horarios" path="/administracion/horarios" element={<HorariosAdmin />} />,
            <Route key="cursos" path="/administracion/cursos" element={<CursosAdmin />} />,
            <Route key="carga-academica" path="/administracion/carga-academica" element={<CargaAcademica />} />,
            <Route key="direccion-curso" path="/administracion/direccion-curso" element={<DireccionCursoAdmin />} />,
            <Route key="mallas" path="/mallas" element={<MallasList user={user} />} />,
            <Route key="malla-tablero" path="/mallas/tablero" element={<MallasDashboard user={user} />} />,
            <Route key="malla-editor" path="/mallas/:id" element={<MallaEditor user={user} />} />,
            <Route key="cuadro-honor" path="/cuadro-honor" element={<CuadroHonor user={user} />} />,
            <Route key="contenidos" path="/configuracion/contenidos" element={<ContenidosAdmin />} />,
            <Route key="escalas-notas" path="/configuracion/escalas-notas" element={<EscalasNotasAdmin />} />,
            <Route key="nota-aprobacion" path="/configuracion/nota-aprobacion" element={<NotaAprobacionAdmin />} />,
            <Route key="periodos" path="/configuracion/periodos" element={<PeriodosAdmin />} />,
            <Route key="config-notas" path="/configuracion/notas" element={<ConfigNotasAdmin />} />,
            <Route key="config-aula-virtual" path="/configuracion/aula-virtual" element={<ConfigAulaVirtualAdmin />} />,
            <Route key="config-financiero" path="/configuracion/financiero" element={<ConfigFinancieroAdmin />} />,
            <Route key="modulos-perfil" path="/configuracion/modulos-perfil" element={<ModulosPerfilAdmin />} />,
            <Route key="bloquear-notas" path="/configuracion/bloquear-notas" element={<BloquearNotasAdmin />} />,
            <Route key="usuarios" path="/usuarios" element={<UsuariosColegio />} />,
            <Route key="prematriculas" path="/prematriculas" element={<PrematriculasAdmin user={user} />} />,
            <Route key="financiero" path="/financiero" element={<FinancieroAdmin user={user} />} />,
            <Route key="circulares" path="/circulares" element={<CircularesAdmin user={user} />} />,
            <Route key="elecciones" path="/elecciones" element={<EleccionesAdmin user={user} />} />,
            <Route key="inasistencias" path="/inasistencias" element={<InasistenciasAdmin user={user} />} />,
            <Route key="observaciones" path="/observaciones" element={<ObservacionesDocente user={user} />} />,
            <Route key="biblioteca" path="/biblioteca" element={<BibliotecaDocente user={user} />} />,
            <Route key="agendamientos" path="/agendamientos" element={<AgendamientosAdmin user={user} />} />,
            <Route key="encuestas" path="/encuestas" element={<EncuestasAdmin user={user} />} />,
            <Route key="exportar-datos" path="/exportar-datos" element={<ExportarDatosAdmin user={user} />} />,
            <Route key="soporte" path="/soporte" element={<SoporteAdmin user={user} />} />,
            <Route key="seguimiento" path="/seguimiento" element={<SeguimientoAdmin user={user} />} />,
            <Route key="boletines-redirect" path="/boletines" element={<Navigate to="/boletines/generar" replace />} />,
            <Route key="generar-boletines" path="/boletines/generar" element={<GenerarBoletines user={user} />} />,
            <Route key="fotos-estudiantes" path="/boletines/fotos" element={<FotosEstudiantesAdmin />} />,
            <Route key="config-boletines" path="/boletines/configuracion" element={<ConfigBoletines user={user} />} />,
            <Route key="carnets" path="/carnets" element={<CarnetsAdmin user={user} />} />,
            <Route key="certificados" path="/certificados" element={<GenerarCertificados user={user} />} />,
            <Route key="caracterizacion-admin" path="/configuracion/caracterizacion" element={<CaracterizacionAdmin user={user} />} />,
            <Route key="caracterizacion-v2-admin" path="/configuracion/caracterizacion-v2" element={<CaracterizacionV2Admin user={user} />} />,
            <Route key="reportes-redirect" path="/reportes" element={<Navigate to="/reportes/global" replace />} />,
            <Route key="reporte-global" path="/reportes/global" element={<ReporteGlobalAdmin user={user} />} />,
            <Route key="reporte-notas" path="/reportes/notas" element={<ReporteNotasDocente user={user} />} />,
            <Route key="sabana-notas" path="/reportes/sabana" element={<SabanaNotasDocente user={user} />} />,
            <Route key="promedios-acumulados" path="/reportes/promedios" element={<PromediosAcumuladosDocente user={user} />} />,
            <Route key="reprobados" path="/reportes/reprobados" element={<ReprobadosDocente user={user} />} />
          ]}

          {/* ===== RUTAS DOCENTE ===== */}
          {user.role === 'Docente' && [
            <Route key="dashboard" path="/dashboard" element={<DashboardDocente user={user} />} />,
            <Route key="notas" path="/notas" element={<NotasDocente user={user} />} />,
            <Route key="mallas" path="/mallas" element={<MallasList user={user} />} />,
            <Route key="malla-editor" path="/mallas/:id" element={<MallaEditor user={user} />} />,
            <Route key="aula-virtual" path="/aula-virtual" element={<AulaVirtualDocente user={user} />} />,
            <Route key="inasistencias" path="/inasistencias" element={<InasistenciasDocente user={user} />} />,
            <Route key="agenda" path="/agenda" element={<AgendaDocente user={user} />} />,
            <Route key="biblioteca" path="/biblioteca" element={<BibliotecaDocente user={user} />} />,
            <Route key="observaciones" path="/observaciones" element={<ObservacionesDocente user={user} />} />,
            <Route key="agendamientos" path="/agendamientos" element={<AgendamientosDocente user={user} />} />,
            <Route key="nivelaciones" path="/nivelaciones" element={<NivelacionesDocente user={user} />} />,
            <Route key="soporte" path="/soporte" element={<SoporteDocente user={user} />} />,
            <Route key="boletines-redirect" path="/boletines" element={<Navigate to="/boletines/vista-previa" replace />} />,
            <Route key="vista-previa-boletines" path="/boletines/vista-previa" element={<VistaPreviaBoletinesDocente user={user} />} />,
            <Route key="desempenos" path="/boletines/desempenos" element={<DesempenosDocente user={user} />} />,
            <Route key="observaciones-boletin" path="/boletines/observaciones" element={<ObservacionesBoletinDocente user={user} />} />,
            <Route key="reportes-redirect" path="/reportes" element={<Navigate to="/reportes/notas" replace />} />,
            <Route key="reporte-notas" path="/reportes/notas" element={<ReporteNotasDocente user={user} />} />,
            <Route key="sabana-notas" path="/reportes/sabana" element={<SabanaNotasDocente user={user} />} />,
            <Route key="promedios-acumulados" path="/reportes/promedios" element={<PromediosAcumuladosDocente user={user} />} />,
            <Route key="reprobados" path="/reportes/reprobados" element={<ReprobadosDocente user={user} />} />,
            <Route key="salas-virtuales" path="/salas-virtuales" element={<SalasVirtualesDocente user={user} />} />,
            <Route key="elecciones" path="/elecciones" element={<EleccionesDocente user={user} />} />,
            <Route key="encuestas" path="/encuestas" element={<EncuestasDocente user={user} />} />,
            <Route key="circulares" path="/circulares" element={<CircularesDocente user={user} />} />,
            <Route key="caracterizacion-redirect" path="/caracterizacion" element={<Navigate to="/caracterizacion/inicial" replace />} />,
            <Route key="caracterizacion-inicial" path="/caracterizacion/inicial" element={<CaracterizacionDocente user={user} />} />,
            <Route key="caracterizacion-v2" path="/caracterizacion/v2" element={<CaracterizacionV2Docente user={user} />} />
          ]}

          {/* ===== RUTAS SOPORTE N1 ===== */}
          {user.role === 'Soporte1' && user.tipoUsuario === 'SISTEMA' && [
            <Route key="bandeja" path="/bandeja" element={<BandejaSoporte1 user={user} />} />,
            <Route key="dashboard" path="/dashboard" element={<DashboardSoporte user={user} />} />
          ]}

          {/* ===== RUTAS SOPORTE N2 ===== */}
          {user.role === 'Soporte2' && user.tipoUsuario === 'SISTEMA' && [
            <Route key="bandeja" path="/bandeja" element={<BandejaSoporte2 user={user} />} />,
            <Route key="dashboard" path="/dashboard" element={<DashboardSoporte user={user} />} />
          ]}

          {/* ===== RUTAS ESTUDIANTE ===== */}
          {user.role === 'Estudiante' && user.bloqueado && [
            // Estudiante bloqueado por pagos: solo acceso a Aula Virtual (derecho a la educación)
            <Route key="aula-virtual" path="/aula-virtual" element={<AulaVirtualEstudiante user={user} />} />,
            <Route key="redirect-bloqueado" path="*" element={<Navigate to="/aula-virtual" replace />} />
          ]}
          {user.role === 'Estudiante' && !user.bloqueado && encuestasPendientes > 0 && [
            // Si hay encuestas pendientes, SOLO mostrar la ruta de encuestas y redirigir todo lo demás
            <Route key="encuestas" path="/encuestas" element={<EncuestasEstudiante user={user} onEncuestasPendientes={setEncuestasPendientes} />} />,
            <Route key="redirect-encuestas" path="*" element={<Navigate to="/encuestas" replace />} />
          ]}
          {user.role === 'Estudiante' && !user.bloqueado && encuestasPendientes === 0 && prematriculaPendiente && [
            // Prematrícula obligatoria sin responder: SOLO la pantalla de prematrícula hasta que responda
            <Route key="prematricula" path="/prematricula" element={<PrematriculaEstudiante user={user} obligatoria onEstadoCambio={setPrematriculaPendiente} />} />,
            <Route key="redirect-prematricula" path="*" element={<Navigate to="/prematricula" replace />} />
          ]}
          {user.role === 'Estudiante' && !user.bloqueado && encuestasPendientes === 0 && !prematriculaPendiente && [
            <Route key="perfil" path="/perfil" element={<PerfilEstudiante user={user} />} />,
            <Route key="dashboard" path="/dashboard" element={<DashboardEstudiante />} />,
            <Route key="notas" path="/notas" element={<NotasEstudiante />} />,
            <Route key="fallas" path="/fallas" element={<FallasEstudiante user={user} />} />,
            <Route key="agenda" path="/agenda" element={<AgendaEstudiante user={user} />} />,
            <Route key="horario" path="/horario" element={<HorarioEstudiante />} />,
            <Route key="biblioteca" path="/biblioteca" element={<BibliotecaEstudiante user={user} />} />,
            <Route key="aula-virtual" path="/aula-virtual" element={<AulaVirtualEstudiante user={user} />} />,
            <Route key="observaciones" path="/observaciones" element={<ObservacionesEstudiante user={user} />} />,
            <Route key="circulares" path="/circulares" element={<CircularesEstudiante user={user} />} />,
            <Route key="financiero" path="/financiero" element={<FinancieroEstudiante user={user} />} />,
            <Route key="agendamientos" path="/agendamientos" element={<AgendamientosEstudiante user={user} />} />,
            <Route key="elecciones" path="/elecciones" element={<EleccionesEstudiante user={user} />} />,
            <Route key="encuestas" path="/encuestas" element={<EncuestasEstudiante user={user} onEncuestasPendientes={setEncuestasPendientes} />} />,
            <Route key="boletines" path="/boletines" element={<BoletinesEstudiante user={user} />} />,
            <Route key="prematricula" path="/prematricula" element={<PrematriculaEstudiante user={user} onEstadoCambio={setPrematriculaPendiente} />} />
          ]}

          {/* Ruta por defecto - redirigir */}
          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
        </Suspense>
      </Layout>

      {showWarningModal && (
        <SessionWarningModal
          remainingSeconds={remainingSeconds}
          onExtend={extendSession}
          onLogout={confirmLogout}
        />
      )}

      {showCambiarPassword && (
        <CambiarPasswordModal
          isOpen={showCambiarPassword}
          onClose={() => setShowCambiarPassword(false)}
          onSuccess={handlePasswordChanged}
          obligatorio={user?.debeCambiarPassword}
        />
      )}
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
